import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { MobxComponent } from '../../../../../mobx/components';
import { Button, Input, RotateLoader } from '@lib/components';
import { Modal } from '@lib/components';
import { ModalTitle, ModalContent } from '@lib/components';
import { FormGroup } from '@lib/components';
import { config } from '../../../../../config';

const IconFile = () => (
	<svg width={32} enable-background="new 0 0 40 40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><path d="m30.918 15.983h-.678v-3.271c0-.02-.003-.041-.006-.062-.001-.13-.043-.257-.132-.358l-5.442-6.217c-.002-.002-.003-.002-.004-.004-.033-.036-.07-.066-.11-.092-.012-.008-.024-.015-.036-.022-.035-.019-.071-.035-.109-.046-.01-.003-.02-.007-.03-.01-.041-.01-.084-.016-.127-.016h-13.377c-.611 0-1.107.497-1.107 1.107v8.99h-.678c-.874 0-1.582.708-1.582 1.582v8.228c0 .873.709 1.582 1.582 1.582h.678v5.632c0 .61.496 1.107 1.107 1.107h18.266c.61 0 1.107-.497 1.107-1.107v-5.632h.678c.873 0 1.582-.708 1.582-1.582v-8.228c0-.873-.708-1.581-1.582-1.581zm-20.051-8.991h12.823v5.664c0 .306.248.553.554.553h4.89v2.773h-18.267zm8.282 15.168c-1.344-.48-2.231-1.224-2.231-2.399 0-1.379 1.164-2.422 3.059-2.422.924 0 1.583.18 2.063.407l-.408 1.463c-.312-.155-.899-.383-1.679-.383-.792 0-1.176.371-1.176.779 0 .516.443.743 1.499 1.139 1.428.528 2.087 1.271 2.087 2.411 0 1.356-1.031 2.507-3.25 2.507-.924 0-1.835-.252-2.291-.504l.372-1.499c.479.252 1.247.504 2.026.504.84 0 1.284-.349 1.284-.876 0-.504-.383-.791-1.355-1.127zm-9.526-.552c0-2.747 1.967-4.27 4.413-4.27.948 0 1.667.191 1.991.348l-.384 1.451c-.359-.156-.876-.3-1.535-.3-1.439 0-2.566.875-2.566 2.674 0 1.607.959 2.627 2.578 2.627.564 0 1.164-.107 1.535-.264l.265 1.439c-.324.155-1.092.348-2.063.348-2.795.001-4.234-1.75-4.234-4.053zm19.51 11.1h-18.266v-5.333h18.266zm-1.478-7.166h-2.135l-2.59-8.084h2.003l.983 3.419c.275.971.527 1.883.719 2.89h.036c.204-.971.456-1.931.731-2.854l1.032-3.454h1.942z" fill="#1a171b"/></svg>
);

interface Props {
	autoOpen?: boolean;
	hideTrigger?: boolean;
	onClose?: () => void;
}
interface State {
	active: boolean;
	selectedCSVFile: any;
	importMode: T.API.MenuImportMode;
	preview: T.API.MenuImportSummary | null;
	textConfirm: string;
	confirmError: boolean;
	loading: boolean;
	uploadError: string;
}

@inject("store") @observer
export class CSVImport extends MobxComponent<Props, State> {
	private fileInputId = `file-csv-input-${Math.random().toString(36).slice(2)}`;

	constructor(props: Props) {
		super(props);
		this.state = {
			active: !!props.autoOpen,
			selectedCSVFile: null,
			importMode: 'add',
			preview: null,
			textConfirm: '',
			confirmError: false,
			loading: false,
			uploadError: '',
		};
	}

	handleCSVUpload(file: File) {
		this.setState({
			selectedCSVFile: file,
			preview: null,
			uploadError: '',
		});
	}

	resetModal = () => {
		this.setState({
			active: false,
			selectedCSVFile: null,
			importMode: 'add',
			preview: null,
			textConfirm: '',
			confirmError: false,
			loading: false,
			uploadError: '',
		});
		if (this.props.onClose) {
			this.props.onClose();
		}
	};

	setImportMode(importMode: T.API.MenuImportMode) {
		const restaurant = this.injected.store.restaurant;
		this.setState({
			importMode,
			preview: null,
			textConfirm: '',
			confirmError: false,
			uploadError: '',
		}, () => {
			if (restaurant) {
				this.uploadCSV(restaurant._id, true);
			}
		});
	}

	async uploadCSV(restaurant_id: any, dryRun: boolean) {
		const { selectedCSVFile, importMode } = this.state;
		const authToken = this.injected.store.auth.token;

		if (!selectedCSVFile) {
			this.setState({
				uploadError: 'Please select a CSV file to upload.',
			});
			return false;
		}

		try {
			const formData = new FormData();
			formData.append('upload_files', selectedCSVFile);
			formData.append('mode', importMode);
			formData.append('dryRun', String(dryRun));
			this.setState({
				loading: true,
				uploadError: '',
			});

			const response = await fetch(`${config.urls.api}/restaurants/menu/import/csv/${restaurant_id}`, {
				method: 'POST',
				body: formData,
				headers: {
					'Authorization-Dashboard': `${authToken}`,
				},
			});

			if (response.ok) {
				const result = await response.json();
				if (dryRun) {
					this.setState({
						preview: result.summary,
						loading: false,
					});
					return true;
				}
				window.location.reload();
				return true;
			} else {
				const error: any = await response.json();
				this.setState({
					uploadError: `Failed to upload, please check the CSV file:\n${error.details || error.message || error.error}`,
					loading: false,
				});
				return false;
			}
		} catch (error) {
			console.error('Error uploading CSV:', error);
			this.setState({
				uploadError:'An error occurred while uploading the CSV.',
				loading: false,
			});
			return false;
		}
	}

	confirmImport(restaurant_id: any) {
		const { importMode, textConfirm } = this.state;

		if (importMode === 'overwrite' && textConfirm !== 'IMPORT') {
			this.setState({ confirmError: true });
			return;
		}

		this.uploadCSV(restaurant_id, false);
	}

	renderModeButton(importMode: T.API.MenuImportMode, label: string, description: string) {
		const active = this.state.importMode === importMode && !!this.state.preview;
		return (
			<div style={{ marginBottom: 10 }}>
				<Button
					color={active ? 'primary' : 'primary-inverse'}
					full={true}
					disabled={this.state.loading}
					onClick={() => this.setImportMode(importMode)}>
					{label}
				</Button>
				<p style={{ margin: '6px 0 0', color: '#777', fontSize: 12 }}>{description}</p>
			</div>
		);
	}

	renderPreviewSummary(preview: T.API.MenuImportSummary) {
		const stats = [
			{ label: 'Menus', added: preview.menus.added, updated: preview.menus.updated, removed: preview.menus.removed },
			{ label: 'Categories', added: preview.categories.added, updated: preview.categories.updated, removed: preview.categories.removed },
			{ label: 'Dishes', added: preview.dishes.added, updated: preview.dishes.updated, removed: preview.dishes.removed },
		];

		return (
			<div style={{ marginBottom: 16 }}>
				<div style={{
					background: '#fff',
					border: '1px solid #e5e5e5',
					borderRadius: 8,
					boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
					padding: 14,
					marginBottom: 10,
				}}>
					<div style={{ marginBottom: 12 }}>
						<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
							<p style={{ margin: 0, fontWeight: 600 }}>Import preview</p>
							<span style={{
								background: preview.mode === 'add' ? '#eaf7ef' : '#fff3e6',
								borderRadius: 12,
								color: preview.mode === 'add' ? '#2f7d45' : '#a15c00',
								flexShrink: 0,
								fontSize: 11,
								fontWeight: 600,
								padding: '4px 8px',
							}}>
								{preview.mode === 'add' ? 'ADD' : 'OVERWRITE'}
							</span>
						</div>
						<p style={{ margin: '6px 0 0', color: '#777', fontSize: 12 }}>
							{preview.mode === 'add'
								? 'Add mode only creates new rows; existing IDs are left unchanged'
								: 'Overwrite mode removes current rows omitted from the CSV'}
						</p>
					</div>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
						{stats.map(stat => (
							<div
								key={stat.label}
								style={{
									background: '#f8f9fb',
									border: '1px solid #e9edf3',
									borderRadius: 8,
									flex: 1,
									minWidth: 0,
									padding: '10px 6px',
									textAlign: 'center',
								}}>
								<p style={{ margin: '0 0 8px', color: '#333', fontSize: 12, fontWeight: 700 }}>{stat.label}</p>
								<div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
									<div style={{ background: '#edf8f1', borderRadius: 5, color: '#2f7d45', flex: 1, padding: '5px 2px' }}>
										<strong>{stat.added}</strong>
										<div style={{ fontSize: 10 }}>added</div>
									</div>
									<div style={{ background: '#eef4ff', borderRadius: 5, color: '#2d6cdf', flex: 1, padding: '5px 2px' }}>
										<strong>{stat.updated}</strong>
										<div style={{ fontSize: 10 }}>updated</div>
									</div>
									<div style={{ background: '#fff0f0', borderRadius: 5, color: '#bd362f', flex: 1, padding: '5px 2px' }}>
										<strong>{stat.removed}</strong>
										<div style={{ fontSize: 10 }}>removed</div>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
				{preview.duplicates.length > 0 && (
					<div style={{
						background: '#fffaf0',
						border: '1px solid #f0d9a5',
						borderRadius: 8,
						color: '#7a5300',
						padding: 12,
						marginBottom: 10,
					}}>
						<p style={{ margin: '0 0 8px', fontWeight: 600 }}>Possible duplicates</p>
						{preview.duplicates.map((duplicate, index) => (
							<p key={index} style={{ margin: '0 0 5px', fontSize: 12 }}>{duplicate}</p>
						))}
					</div>
				)}
			</div>
		);
	}

	render() {
		const { active, importMode, preview, textConfirm, confirmError, selectedCSVFile, loading, uploadError} = this.state;
		const { restaurant } = this.injected.store;
		return (
			<div className="inline-block">
				{!this.props.hideTrigger && (
				<Button
					size="xxs"
					color="white"
					onClick={() => this.setState({active: true})}>
					CSV Import
				</Button>
				)}
				<Modal
					active={active}
					width={440}
					close={this.resetModal}
				>
					<ModalTitle className="round-top-sm">
						<h3 className="text-center flex-grow">CSV Import</h3>
					</ModalTitle>
					<ModalContent paddingtb={30} paddinglr={30}>
						<FormGroup>
							<Button
								color="primary-inverse"
								full
								onClick={() => document.getElementById(this.fileInputId)?.click()}>
								Choose file
							</Button>
							{selectedCSVFile &&
								<span style={{display: 'flex', alignItems: 'center'}}>
									<IconFile /> {selectedCSVFile.name}
							</span>}
							<input
								id={this.fileInputId}
								type="file"
								hidden
								accept=".csv"
								onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
									const file = event.target.files?.[0];
									if (file) {
										this.handleCSVUpload(file);
									}
								}}
							/>
						</FormGroup>
						<FormGroup>
							<p style={{ marginBottom: 10 }}>How should this CSV be imported?</p>
							{this.renderModeButton(
								'add',
								'Add to Existing Menu',
								'Only creates new menu, category, or dish rows. Existing items are not updated or removed.'
							)}
							{this.renderModeButton(
								'overwrite',
								'Overwrite Existing Menu',
								'Replaces the current menu data with the CSV result.'
							)}
						</FormGroup>
						{preview && this.renderPreviewSummary(preview)}
						{uploadError && <p style={{color: '#bd362f', marginBottom: 16, whiteSpace: 'pre-wrap'}}>{uploadError}</p>}
						{preview && importMode === 'overwrite' && (
							<div style={{
								background: '#fff4f4',
								border: '1px solid #f0b8b8',
								borderRadius: 8,
								marginBottom: 16,
								padding: 14,
							}}>
								<p style={{ margin: '0 0 8px', color: '#8a1f1a', fontWeight: 700 }}>Confirm Menu Overwrite</p>
								<p style={{ margin: '0 0 12px', color: '#8a1f1a', fontSize: 13 }}>
									This will replace your existing menu with the CSV import and cannot be undone. Type IMPORT below to confirm and continue.
								</p>
								<Input
									type="text"
									value={textConfirm}
									placeholder="IMPORT"
									onChange={(e) => this.setState({ textConfirm: e.target.value, confirmError: false })}
									onKeyUp={(e) => {
										if (e.which === 13) {
											this.confirmImport(restaurant!._id);
										}
									}}
								/>
								{confirmError && (
									<p style={{ color: '#bd362f', margin: '8px 0 0' }}>Type IMPORT to continue.</p>
								)}
							</div>
						)}
						{loading && !preview && (
							<div style={{ marginBottom: 16, textAlign: 'center' }}>
								<RotateLoader size={2} />
							</div>
						)}
						{preview && (
							<Button
								color="primary"
								full={true}
								loading={loading}
								onClick={() => this.confirmImport(restaurant!._id)}
							>
								{loading && <RotateLoader size={2} />}
								{!loading && 'Import'}
							</Button>
						)}
					</ModalContent>
				</Modal>
			</div>
		);
	}

}
